import { useState, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CallDialer — reusable click-to-call modal with Q&A overlay.
 *
 * Props:
 *  - open: bool                 — controls visibility
 *  - onClose: () => void
 *  - title: string              — e.g. "Call Amber Grant Foundation"
 *  - subtitle: string           — e.g. "Sponsor: WomensNet | Fit 95"
 *  - phone: string              — e.g. "(817) 381-8831" or empty
 *  - email: string              — fallback contact
 *  - context: {                 — what Shannon's calling about
 *      type: 'grant'|'vendor'|'prospect'|'lead_paint',
 *      record_id: string,
 *      record_label: string,
 *    }
 *  - qa: [{ q, a }]             — pre-loaded Q&A bullets
 *  - talking_points: [string]   — additional bullets to read
 *
 * On submit, logs the call to db.call_logs via POST /api/admin/call-log.
 */
const CallDialer = ({ open, onClose, title, subtitle, phone, email, context, qa, talking_points }) => {
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState("no_answer");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [callStarted, setCallStarted] = useState(false);

  // ── Live AI Assist (speech-to-text + Claude streaming) ──
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState([]);  // array of {role: 'me'|'them', text}
  const [interim, setInterim] = useState("");
  const [askedQuestion, setAskedQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiThinking, setAiThinking] = useState(false);
  const [recognitionRef, setRecognitionRef] = useState(null);
  const [speechSupported, setSpeechSupported] = useState(true);

  useEffect(() => {
    if (!open) {
      // Stop any active recognition on close
      if (recognitionRef) {
        try { recognitionRef.stop(); } catch (_) { /* ignore */ }
      }
      setListening(false);
      setNotes("");
      setOutcome("no_answer");
      setToast(null);
      setCallStarted(false);
      setTranscript([]);
      setInterim("");
      setAskedQuestion("");
      setAiAnswer("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleListening = () => {
    if (listening && recognitionRef) {
      try { recognitionRef.stop(); } catch (_) { /* ignore */ }
      setListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechSupported(false);
      setToast({ ok: false, msg: "Live listen needs Chrome on laptop. Safari + Firefox don't support it yet." });
      return;
    }
    const recog = new SpeechRecognition();
    recog.continuous = true;
    recog.interimResults = true;
    recog.lang = "en-US";
    recog.onresult = (event) => {
      let finalText = "";
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += t + " ";
        } else {
          interimText += t;
        }
      }
      if (finalText.trim()) {
        setTranscript((prev) => [...prev, { role: "heard", text: finalText.trim(), at: new Date() }]);
        setInterim("");
      } else {
        setInterim(interimText);
      }
    };
    recog.onerror = (e) => {
      console.error("Speech recognition error:", e.error);
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setToast({ ok: false, msg: "Microphone permission denied. Click the 🔒 in your address bar → Allow." });
      }
      setListening(false);
    };
    recog.onend = () => {
      // Auto-restart if user didn't explicitly stop
      setListening((isListening) => {
        if (isListening) {
          try { recog.start(); } catch (_) { /* ignore */ }
          return true;
        }
        return false;
      });
    };
    recog.start();
    setRecognitionRef(recog);
    setListening(true);
    setToast({ ok: true, msg: "🎙️ Listening — put the call on speakerphone." });
    setTimeout(() => setToast(null), 4000);
  };

  const askAI = async (overrideQuestion) => {
    const q = (overrideQuestion || askedQuestion || (transcript.length > 0 ? transcript[transcript.length - 1].text : "")).trim();
    if (!q) {
      setToast({ ok: false, msg: "Hit 'Use Last Heard' or type the question first." });
      return;
    }
    setAiAnswer("");
    setAiThinking(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/dialer-assist`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: q,
          recipient_context: subtitle || title,
          history: transcript.slice(-6).map(t => t.text),
        }),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setAiAnswer(`⚠️ ${d.detail || "Assist failed."}`);
        setAiThinking(false);
        return;
      }
      const full = d.answer || "";
      // Teletype effect — render character by character for that "live" feel
      setAiThinking(false);
      for (let i = 0; i <= full.length; i++) {
        await new Promise(r => setTimeout(r, 14));
        setAiAnswer(full.slice(0, i));
      }
    } catch (e) {
      setAiAnswer(`⚠️ Network error: ${String(e).slice(0, 200)}`);
      setAiThinking(false);
    }
  };

  if (!open) return null;

  const startCall = () => {
    setCallStarted(true);
    if (phone) {
      const cleaned = phone.replace(/[^\d+]/g, "");
      // tel: link opens Phone Link / iMessage / Skype on laptops with linked phones
      window.open(`tel:${cleaned}`, "_self");
    }
  };

  const saveLog = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/call-log`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          context_type: context?.type || "other",
          record_id: context?.record_id || "",
          record_label: context?.record_label || title,
          phone: phone || "",
          email: email || "",
          outcome,
          notes,
        }),
      });
      if (r.ok) {
        setToast({ ok: true, msg: "Call logged." });
        setTimeout(() => { onClose(); }, 800);
      } else {
        const d = await r.json().catch(() => ({}));
        setToast({ ok: false, msg: d.detail || "Save failed." });
      }
    } catch (e) {
      setToast({ ok: false, msg: "Network error." });
    }
    setSaving(false);
  };

  return (
    <div data-testid="call-dialer-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         style={{
           position: "fixed", inset: 0, background: "rgba(8,13,28,0.82)",
           display: "flex", alignItems: "center", justifyContent: "center",
           zIndex: 9500, padding: "1rem",
         }}>
      <div style={{
        background: "#0f172a", border: "1px solid rgba(184,145,90,0.5)", borderLeft: "4px solid #B8915A",
        borderRadius: 10, padding: "1.4rem 1.6rem", width: "100%", maxWidth: 620,
        maxHeight: "92vh", overflowY: "auto", boxShadow: "0 28px 60px rgba(0,0,0,0.55)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.6rem", marginBottom: "0.8rem" }}>
          <div>
            <div style={{ fontSize: "0.66rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#B8915A", fontWeight: 700 }}>
              📞 Call Dialer
            </div>
            <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "1.1rem", marginTop: 4 }}>{title}</div>
            {subtitle && <div style={{ color: "#9CA3AF", fontSize: "0.84rem", marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose}
                  data-testid="call-dialer-close"
                  style={{ background: "transparent", border: "none", color: "#9CA3AF", fontSize: "1.5rem", cursor: "pointer", lineHeight: 1 }}>
            ×
          </button>
        </div>

        {/* Call action row */}
        <div style={{
          display: "flex", gap: "0.6rem", flexWrap: "wrap", padding: "0.85rem 1rem",
          background: "rgba(184,145,90,0.08)", border: "1px solid rgba(184,145,90,0.3)", borderRadius: 8, marginBottom: "0.9rem"
        }}>
          <div style={{ flex: "1 1 200px" }}>
            <div style={{ color: "#9CA3AF", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Phone</div>
            <div style={{ color: "#F3F4F6", fontSize: "1.05rem", fontWeight: 700, marginTop: 2 }}>{phone || "—"}</div>
            {email && <div style={{ color: "#86EFAC", fontSize: "0.82rem", marginTop: 4 }}>📧 {email}</div>}
          </div>
          <button type="button" onClick={startCall}
                  disabled={!phone}
                  data-testid="call-dialer-start"
                  style={{
                    background: phone ? (callStarted ? "#5e7263" : "#00DC78") : "#4B5563",
                    color: phone ? "#0F172A" : "#9CA3AF",
                    border: "none", padding: "0.65rem 1.4rem", borderRadius: 6,
                    fontWeight: 700, fontSize: "0.95rem", cursor: phone ? "pointer" : "not-allowed",
                    alignSelf: "center"
                  }}>
            {callStarted ? "📞 In Progress" : "📞 Place Call"}
          </button>
        </div>

        {/* Talking points */}
        {talking_points && talking_points.length > 0 && (
          <div data-testid="call-dialer-talking-points" style={{
            background: "rgba(184,145,90,0.06)", border: "1px solid rgba(184,145,90,0.3)", borderRadius: 6,
            padding: "0.7rem 0.9rem", marginBottom: "0.7rem"
          }}>
            <div style={{ color: "#B8915A", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 5 }}>
              🎤 Talking Points (read while dialing)
            </div>
            <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "#D1D5DB", fontSize: "0.86rem", lineHeight: 1.55 }}>
              {talking_points.map((p, i) => <li key={i} style={{ marginBottom: 4 }}>{p}</li>)}
            </ul>
          </div>
        )}

        {/* Q&A */}
        {qa && qa.length > 0 && (
          <div data-testid="call-dialer-qa" style={{
            background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 6,
            padding: "0.7rem 0.9rem", marginBottom: "0.7rem"
          }}>
            <div style={{ color: "#86EFAC", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 5 }}>
              💬 Likely Q&amp;A
            </div>
            {qa.map((item, i) => (
              <div key={i} style={{ marginBottom: 9 }}>
                <div style={{ color: "#86EFAC", fontWeight: 700, fontSize: "0.85rem" }}>Q: {item.q}</div>
                <div style={{ color: "#D1D5DB", fontSize: "0.84rem", marginTop: 2, lineHeight: 1.5 }}>{item.a}</div>
              </div>
            ))}
          </div>
        )}

        {/* 🤖 LIVE AI ASSIST (speech-to-text + Claude streaming) */}
        <div data-testid="call-dialer-ai-assist" style={{
          background: "linear-gradient(135deg, rgba(184,145,90,0.12) 0%, rgba(122,143,126,0.1) 100%)",
          border: "1px solid rgba(184,145,90,0.4)", borderLeft: "4px solid #B8915A",
          borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "0.8rem"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "#B8915A", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                🤖 Live AI Assist
              </div>
              <div style={{ color: "#9CA3AF", fontSize: "0.78rem", marginTop: 2, lineHeight: 1.4 }}>
                Put the call on speakerphone, hit 🎙️ Listen, and Claude will answer questions in real time using your Moovin Forward facts.
              </div>
            </div>
            <button type="button" onClick={toggleListening}
                    data-testid="call-dialer-listen-toggle"
                    style={{
                      background: listening ? "#FCA5A5" : "#86EFAC",
                      color: "#0F172A", border: "none", padding: "0.5rem 1rem", borderRadius: 6,
                      fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", whiteSpace: "nowrap",
                    }}>
              {listening ? "⏹ Stop" : "🎙️ Listen"}
            </button>
          </div>

          {/* Live transcript pane */}
          {(transcript.length > 0 || interim) && (
            <div data-testid="call-dialer-transcript" style={{
              maxHeight: 130, overflowY: "auto", marginTop: "0.6rem",
              background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 5, padding: "0.5rem 0.7rem", fontSize: "0.82rem", color: "#D1D5DB", lineHeight: 1.5
            }}>
              {transcript.map((t, i) => (
                <div key={i} style={{ marginBottom: 4 }}>
                  <span style={{ color: "#9CA3AF", fontSize: "0.72rem" }}>{t.at.toLocaleTimeString().slice(0, 5)}</span>{" "}
                  {t.text}
                </div>
              ))}
              {interim && <div style={{ opacity: 0.55, fontStyle: "italic" }}>…{interim}</div>}
            </div>
          )}

          {/* Question / Ask AI */}
          <div style={{ marginTop: "0.6rem", display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
            <input
              type="text" placeholder="Question they just asked (or auto-fill from transcript)"
              value={askedQuestion} onChange={(e) => setAskedQuestion(e.target.value)}
              data-testid="call-dialer-question"
              style={{ flex: "1 1 240px", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.45rem 0.65rem", borderRadius: 5, fontSize: "0.86rem", minWidth: 200 }} />
            <button type="button" onClick={() => {
                       if (transcript.length > 0) setAskedQuestion(transcript[transcript.length - 1].text);
                     }}
                    disabled={transcript.length === 0}
                    data-testid="call-dialer-use-last-heard"
                    style={{ background: "transparent", color: transcript.length > 0 ? "#86EFAC" : "#4B5563", border: `1px solid ${transcript.length > 0 ? "#86EFAC" : "#4B5563"}`, padding: "0.45rem 0.85rem", borderRadius: 5, fontWeight: 700, fontSize: "0.78rem", cursor: transcript.length > 0 ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}>
              ↑ Use Last
            </button>
            <button type="button" onClick={() => askAI()}
                    disabled={aiThinking || (!askedQuestion && transcript.length === 0)}
                    data-testid="call-dialer-ask-ai"
                    style={{ background: aiThinking ? "#4B5563" : "#B8915A", color: "#1b2a4e", border: "1px solid #8a6c3e", padding: "0.45rem 1.1rem", borderRadius: 5, fontWeight: 700, fontSize: "0.85rem", cursor: aiThinking ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
              {aiThinking ? "Thinking…" : "🤖 Ask Advisor"}
            </button>
          </div>

          {/* AI streaming response */}
          {(aiAnswer || aiThinking) && (
            <div data-testid="call-dialer-ai-answer" style={{
              marginTop: "0.6rem", background: "rgba(184,145,90,0.1)", border: "1px solid rgba(184,145,90,0.4)",
              borderRadius: 5, padding: "0.65rem 0.85rem"
            }}>
              <div style={{ color: "#B8915A", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
                {aiThinking && !aiAnswer ? "Reasoning…" : "Read this aloud →"}
              </div>
              <div style={{ color: "#F3F4F6", fontSize: "0.92rem", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                {aiAnswer || "…"}{aiThinking && aiAnswer ? " ▍" : ""}
              </div>
            </div>
          )}

          {!speechSupported && (
            <div style={{ marginTop: 6, color: "#FCA5A5", fontSize: "0.76rem" }}>
              ⚠ Live Listen needs Chrome on a laptop. Other browsers don&rsquo;t support continuous speech recognition yet. You can still hit &ldquo;🤖 Ask AI&rdquo; and type the question manually.
            </div>
          )}
        </div>

        {/* Log the call */}
        <div style={{ marginTop: "0.6rem", paddingTop: "0.8rem", borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <div style={{ color: "#9CA3AF", fontSize: "0.74rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 6 }}>
            📝 Log the Call
          </div>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)}
                  data-testid="call-dialer-outcome"
                  style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.5rem 0.7rem", borderRadius: 5, fontSize: "0.88rem", marginBottom: 6 }}>
            <option value="no_answer">No answer / voicemail</option>
            <option value="left_voicemail">Left voicemail</option>
            <option value="connected_warm">Connected — warm response</option>
            <option value="connected_lukewarm">Connected — lukewarm</option>
            <option value="connected_no">Connected — no fit</option>
            <option value="scheduled_followup">Scheduled follow-up</option>
            <option value="moved_to_next_step">Moved to next step (proposal/site visit)</option>
          </select>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Quick notes: what they said, objections raised, what to do next."
                    rows={3}
                    data-testid="call-dialer-notes"
                    style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.55rem 0.7rem", borderRadius: 5, fontSize: "0.86rem", resize: "vertical", fontFamily: "inherit" }} />

          {toast && (
            <div data-testid="call-dialer-toast" style={{
              background: toast.ok ? "rgba(34,197,94,0.12)" : "rgba(220,38,38,0.12)",
              border: `1px solid ${toast.ok ? "rgba(34,197,94,0.4)" : "rgba(220,38,38,0.4)"}`,
              color: toast.ok ? "#86EFAC" : "#FCA5A5",
              padding: "0.5rem 0.8rem", borderRadius: 5, fontSize: "0.84rem", marginTop: 8
            }}>
              {toast.ok ? "✓ " : "⚠ "}{toast.msg}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <button type="button" onClick={saveLog} disabled={saving}
                    data-testid="call-dialer-save"
                    style={{ background: saving ? "#4B5563" : "#B8915A", color: "#1b2a4e", border: "1px solid #8a6c3e",
                             padding: "0.6rem 1.4rem", borderRadius: 5, fontWeight: 700, fontSize: "0.9rem",
                             cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Saving…" : "💾 Save Call Log"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallDialer;
